import { H2, H3 } from '@triumph/design-system';
import { AccordionTab } from 'primereact/accordion';

import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';
import { ProviderReasonCodes } from '@/components/shared/api/endpoints/applicant';
import {
    useApplicantReasonCodesQuery,
    useApplicantTagsQuery,
} from '@/components/shared/api/queries/applicant';
import { NoMarginParagraph } from '@/components/shared/components/common.StyledComponents';
import { TabDataState } from '@/components/shared/components/TabDataState';

import {
    AccordionIcon,
    AccordionTabContentWrapper,
    AccordionTabWrapper,
    ApplicantTagsWrapper,
    JourneyWrapper,
    ReasonCode,
    ReasonCodesWrapper,
    StyledAccordion,
    StyledDescription,
} from './TagsList.StyledComponents';

const TAGS_LOAD_ERROR_MESSAGE = 'Could not load tags and signals.';
const TAGS_EMPTY_MESSAGE = 'No tags/signals found for this applicant.';
const REASON_CODES_LOAD_ERROR_MESSAGE = 'Could not load reason codes.';
const REASON_CODES_EMPTY_MESSAGE = 'No reason codes found.';

const renderReasonCodes = (providerReasonCodes: ProviderReasonCodes[], accordionKey: string) => {
    return providerReasonCodes.map(({ providerName, reasonCodes }, i) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key -- read-only API rows with no id
        <div key={`${accordionKey}-content-${i}`}>
            <NoMarginParagraph style={{ fontSize: 13 }}>{providerName}</NoMarginParagraph>
            <AccordionTabWrapper>
                {reasonCodes.map(({ code, description }, j) => (
                    // eslint-disable-next-line @eslint-react/no-array-index-key
                    <AccordionTabContentWrapper key={`${accordionKey}-content-${i}-detail-${j}`}>
                        <ReasonCode>{code}</ReasonCode>
                        <NoMarginParagraph style={{ padding: '8px 0' }}>
                            {description}
                        </NoMarginParagraph>
                    </AccordionTabContentWrapper>
                ))}
            </AccordionTabWrapper>
        </div>
    ));
};

export const TagsList = () => {
    const { applicantId } = useApplicantContext();

    const {
        data: tags,
        isLoading,
        isLoadingError,
        refetch,
    } = useApplicantTagsQuery({
        applicantId: applicantId ?? '',
    });

    const {
        data: reasonCodes,
        isLoading: isLoadingReasonCodes,
        isLoadingError: isReasonCodesLoadingError,
        refetch: refetchReasonCodes,
    } = useApplicantReasonCodesQuery({
        applicantId: applicantId ?? '',
    });

    return (
        <TabDataState
            isLoading={isLoading}
            isLoadingError={isLoadingError}
            onRetry={() => refetch()}
            isEmpty={!tags || tags.length === 0}
            emptyMessage={TAGS_EMPTY_MESSAGE}
            errorMessage={TAGS_LOAD_ERROR_MESSAGE}
        >
            <ApplicantTagsWrapper>
                {(tags ?? []).map(({ entityName, workflowName, evaluationToken, tags }) => {
                    const entityReasonCodes = (reasonCodes ?? []).filter(
                        (reasonCode) =>
                            reasonCode.evaluationToken === evaluationToken &&
                            reasonCode.serviceReasonCodes.length > 0,
                    );

                    return (
                        <div key={evaluationToken}>
                            <H2>{entityName}</H2>
                            <JourneyWrapper>
                                <H3>{workflowName}</H3>
                                <div style={{ display: 'flex', gap: 32, fontSize: 16 }}>
                                    <div>
                                        {tags.map(({ tagName, tagColor }, j) => (
                                            <StyledDescription
                                                // eslint-disable-next-line @eslint-react/no-array-index-key
                                                key={`${evaluationToken}-tag-${j}`}
                                                color={tagColor}
                                            >
                                                <NoMarginParagraph>{tagName}</NoMarginParagraph>
                                            </StyledDescription>
                                        ))}
                                    </div>
                                    <ReasonCodesWrapper>
                                        <TabDataState
                                            isLoading={isLoadingReasonCodes}
                                            isLoadingError={isReasonCodesLoadingError}
                                            onRetry={() => refetchReasonCodes()}
                                            isEmpty={entityReasonCodes.length === 0}
                                            emptyMessage={REASON_CODES_EMPTY_MESSAGE}
                                            errorMessage={REASON_CODES_LOAD_ERROR_MESSAGE}
                                        >
                                            <StyledAccordion
                                                multiple
                                                expandIcon={
                                                    <AccordionIcon className="pi pi-angle-down" />
                                                }
                                                collapseIcon={
                                                    <AccordionIcon className="pi pi-angle-up" />
                                                }
                                            >
                                                {entityReasonCodes.map((reasonCode) =>
                                                    reasonCode.serviceReasonCodes.map((item, j) => {
                                                        const accordionKey = `${evaluationToken}-accordion-${j}`;
                                                        return (
                                                            <AccordionTab
                                                                key={accordionKey}
                                                                header={item.groupName}
                                                            >
                                                                <AccordionTabWrapper>
                                                                    {renderReasonCodes(
                                                                        item.providerReasonCodes,
                                                                        accordionKey,
                                                                    )}
                                                                </AccordionTabWrapper>
                                                            </AccordionTab>
                                                        );
                                                    }),
                                                )}
                                            </StyledAccordion>
                                        </TabDataState>
                                    </ReasonCodesWrapper>
                                </div>
                            </JourneyWrapper>
                        </div>
                    );
                })}
            </ApplicantTagsWrapper>
        </TabDataState>
    );
};
